/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

export const primitives = {
  white: '#FFFFFF',
  white10op75: '#FFFFFFBF',
  grey11: '#F2F4F4',
  grey12: '#E8EAEC',
  grey14: '#D7DADE',
  grey24: '#9EA6AD',
  grey30: '#8D959F',
  grey45: '#6A7883',
  grey57: '#586874',
  grey70: '#4A5B68',
  grey82: '#3F515E',
  grey100: '#334451',
  grey100op75: '#334451BF',
  grey125: '#273540',
  blue12: '#E0EBF5',
  blue45: '#2B7ABC',
  blue57: '#0E68B3',
  blue70: '#0A5A9E',
  blue82: '#09508C',
  green12: '#DCEEE4',
  green45: '#03893D',
  green57: '#027634',
  green70: '#02672D',
  green82: '#015B28',
  orange12: '#FCE5D9',
  orange30: '#F06E26',
  orange45: '#CF4A00',
  orange57: '#B34000',
  orange70: '#9C3800',
  orange82: '#8B3200',
  red12: '#FCE4E5',
  red45: '#E62429',
  red57: '#C71F23',
  red70: '#AE1B1F',
  red82: '#9B181C',
  sea12: '#DAEEEF',
  sea30: '#37A1AA',
  sea35: '#1E95A0',
  sea40: '#0A8C97',
  sea45: '#00828E',
  sea50: '#007B86',
  sea57: '#00717B',
  sea70: '#00626B',
  sea90: '#005158',
  sea110: '#004349',
  violet12: '#F1E6F5',
  violet30: '#B57FCC',
  violet35: '#AC6FC6',
  violet40: '#A564C2',
  violet45: '#9E58BD',
  violet50: '#994FB9',
  violet57: '#9242B4',
  violet70: '#7F399E',
  violet90: '#682F82',
  violet110: '#56276B'
};
export const additionalPrimitives = {
  rose12: '#FEE4E4',
  rose30: '#FB5D5D',
  rose35: '#FA3F3F',
  rose40: '#FA1A1A',
  rose45: '#ED0000',
  rose50: '#E00000',
  rose57: '#CE0000',
  rose70: '#B50000',
  rose90: '#970000',
  rose110: '#7F0000',
  copper12: '#FFE5D3',
  copper30: '#EE6D15',
  copper35: '#DB6414',
  copper40: '#CD5E12',
  copper45: '#BF5811',
  copper50: '#B45310',
  copper57: '#A54C0F',
  copper70: '#90420D',
  copper90: '#77360B',
  copper110: '#622D09',
  honey12: '#F5E9CA',
  honey30: '#C08A00',
  honey35: '#A57600',
  honey40: '#B07E00',
  honey45: '#996E00',
  honey50: '#916800',
  honey57: '#856000',
  honey70: '#745300',
  honey90: '#5F4400',
  honey110: '#4E3800',
  forest12: '#DFEEE0',
  forest30: '#55A459',
  forest35: '#409945',
  forest40: '#319135',
  forest45: '#27872B',
  forest50: '#248029',
  forest57: '#217526',
  forest70: '#1D6621',
  forest90: '#18541B',
  forest110: '#144516',
  aurora12: '#DAEEE8',
  aurora30: '#38A585',
  aurora35: '#1E9975',
  aurora40: '#0B9069',
  aurora45: '#048660',
  aurora50: '#047F5B',
  aurora57: '#037453',
  aurora70: '#036549',
  aurora90: '#02533C',
  aurora110: '#024531',
  sea12: '#DAEEEF',
  sea30: '#37A1AA',
  sea35: '#1E95A0',
  sea40: '#0A8C97',
  sea45: '#00828E',
  sea50: '#007B86',
  sea57: '#00717B',
  sea70: '#00626B',
  sea90: '#005158',
  sea110: '#004349',
  sky12: '#DDECF3',
  sky30: '#4E9CC0',
  sky35: '#3890B8',
  sky40: '#2887B2',
  sky45: '#197EAB',
  sky50: '#1777A2',
  sky57: '#156D94',
  sky70: '#135F81',
  sky90: '#0F4E6A',
  sky110: '#0D4058',
  ocean12: '#DFEBFB',
  ocean30: '#5694EB',
  ocean35: '#4187E8',
  ocean40: '#317DE6',
  ocean45: '#2573DF',
  ocean50: '#236DD3',
  ocean57: '#2063C1',
  ocean70: '#1C57A8',
  ocean90: '#17478B',
  ocean110: '#133B72',
  violet12: '#F1E6F5',
  violet30: '#B57FCC',
  violet35: '#AC6FC6',
  violet40: '#A564C2',
  violet45: '#9E58BD',
  violet50: '#994FB9',
  violet57: '#9242B4',
  violet70: '#7F399E',
  violet90: '#682F82',
  violet110: '#56276B',
  plum12: '#F7E5F0',
  plum30: '#D473B1',
  plum35: '#CE60A7',
  plum40: '#CA529F',
  plum45: '#C54396',
  plum50: '#C1368F',
  plum57: '#BA2083',
  plum70: '#A31C73',
  plum90: '#87175F',
  plum110: '#70134F',
  stone12: '#EAEAEA',
  stone30: '#939393',
  stone35: '#878787',
  stone40: '#7F7F7F',
  stone45: '#767676',
  stone50: '#6F6F6F',
  stone57: '#666666',
  stone70: '#585858',
  stone90: '#494949',
  stone110: '#3C3C3C'
};